import { z } from "zod";
import { featuresSchema } from "./system.js";
import { wishSearchSettingSchema } from "./wish.js";
/**
 * The login request. Both sides import this: the browser validates before it
 * sends, the server validates before it trusts. One definition means the two
 * cannot drift.
 */
export const loginRequestSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    password: z.string().min(1),
});
/**
 * Who is logged in. Never carries the password hash or the token version.
 *
 * **And what they may ask the model, since 2026-09-28** — resolved by the
 * server from the switches and the account, so the browser draws a button by
 * reading one boolean rather than by knowing the rule. Asked once at startup
 * with the rest of the session, which means a switch thrown now reaches
 * somebody else's open page on their next reload; the route refuses the
 * question in the meantime, so what is late is only the button.
 *
 * Both fields carried defaults for one release, while the browser could
 * arrive before the API that sends them, and were made required on
 * 2026-09-28 once revision 58 was serving them. A session without them is now
 * a broken deploy rather than an older API — and read as *everything on,
 * nobody the administrator* it would have looked correct.
 */
export const sessionUserSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string().email(),
    isAdministrator: z.boolean(),
    features: featuresSchema,
    /**
     * The three answers in each cellar the administrator has answered
     * differently from everybody else, keyed by cellar id — later the same day.
     * A cellar that is not here follows `features`. On the session rather than
     * on each cellar's summary so that one fetch at startup still answers every
     * button, and because the summary is built in four routes that have no
     * reason to know about the switches.
     *
     * Defaulted for one release to *no cellar is different*, and made required
     * the same afternoon once revision 60 was serving it: an API that stopped
     * sending it would otherwise have quietly withdrawn every cellar's own
     * answer and looked correct doing it.
     */
    cellarFeatures: z.record(z.string(), featuresSchema),
    /**
     * Whether *Upptäck* is shown to this account — added 2026-09-28, resolved
     * by the server because *Upptäck* stands in no cellar: the account sees it
     * when any cellar it reaches answers yes (that cellar's own answer, or
     * everybody else's), or, reaching none, when everybody else's does. The
     * rule is `mayDiscover` in `backend/src/http/features.ts`.
     *
     * Defaulted for one release, while the browser could arrive before the API,
     * and made required on 2026-09-29 once revision 64 was serving it: a session
     * without it is a broken deploy, and read as *true* it would show *Upptäck*
     * to accounts the administrator had kept out.
     */
    mayDiscover: z.boolean(),
    /**
     * The weekly search for where the wishes are sold, from 2026-10-17 — see
     * `wishSearchSettingSchema`. Defaulted for one release to *unavailable*,
     * the safe reading: an API that does not send it has no search to switch.
     */
    wishSearch: wishSearchSettingSchema.default({ available: false, enabled: false }),
    /**
     * When this account last said it had read what is new — sent only to an
     * account that owns no cellar, which is who the notes are for, and absent
     * for everybody else. `nullish`, so an API that does not send it means *no
     * notes shown*, the quiet reading.
     */
    releaseNotesSeenAt: z.string().nullish(),
});
export const sessionResponseSchema = z.object({
    user: sessionUserSchema,
});
//# sourceMappingURL=auth.js.map